import { KioskEvse, KioskConnector } from '../../types/assign-kiosk-types';

interface ConnectorRowProps {
  evse: KioskEvse;
  connector: KioskConnector;
  showEvseUid: boolean;
  isLastRow: boolean;
  usedOrders: number[];
  maxOrder: number;
  showOrder: boolean;
  showLabel: boolean;
  labelHeader: string;
  readOnly?: boolean;
  onToggle: () => void;
  onAliasChange: (alias: string) => void;
  onOrderChange: (order: number | null) => void;
}

export function ConnectorRow({
  evse,
  connector,
  showEvseUid,
  isLastRow,
  usedOrders,
  maxOrder,
  showOrder,
  showLabel,
  labelHeader,
  readOnly = false,
  onToggle,
  onAliasChange,
  onOrderChange,
}: ConnectorRowProps) {
  const orderOptions = Array.from({ length: Math.max(maxOrder, 1) }, (_, i) => i + 1);

  const checkbox = (
    <button
      onClick={onToggle}
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors
        ${connector.isSelected
          ? 'bg-yellow-400 border-yellow-400'
          : 'border-2 border-gray-300 bg-white hover:border-yellow-400'
        }`}
    >
      {connector.isSelected && (
        <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  );

  const orderSelect = (
    <select
      value={connector.displayOrder ?? ''}
      onChange={(e) => {
        const val = e.target.value;
        onOrderChange(val === '' ? null : Number(val));
      }}
      className="w-full border-0 border-b border-gray-300 bg-transparent py-0.5 text-sm text-gray-700 focus:border-yellow-400 focus:outline-none"
    >
      <option value="">–</option>
      {orderOptions
        .filter((n) => !usedOrders.includes(n) || n === connector.displayOrder)
        .map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
    </select>
  );

  const nicknameInput = (
    <input
      type="text"
      disabled={!connector.isSelected}
      value={connector.alias}
      maxLength={labelHeader === 'Label' ? 2 : 16}
      onChange={(e) => onAliasChange(e.target.value)}
      placeholder={labelHeader}
      className="w-full border-0 border-b border-gray-300 bg-transparent py-0.5 text-sm text-gray-700 placeholder-gray-300 focus:border-yellow-400 focus:outline-none disabled:cursor-not-allowed disabled:text-gray-300"
    />
  );

  return (
    <>
      {/* ── Desktop: table row ─────────────────────────────────────────── */}
      <tr className={`hidden sm:table-row ${isLastRow ? '' : 'border-b border-gray-100'} ${connector.isSelected ? 'bg-gray-50' : 'bg-white'}`}>
        {!readOnly && <td className="sticky left-0 bg-inherit px-2 sm:px-3 py-2 sm:py-3 w-8">{checkbox}</td>}
        <td className="px-2 sm:px-3 py-2 sm:py-3 w-24 font-mono text-xs sm:text-sm text-gray-700 whitespace-nowrap">
          {showEvseUid ? evse.uid : ''}
        </td>
        <td className="px-2 sm:px-3 py-2 sm:py-3 w-32 font-mono text-xs sm:text-sm text-gray-500 whitespace-nowrap">
          {showEvseUid ? evse.id : ''}
        </td>
        <td className="px-2 sm:px-3 py-2 sm:py-3 w-16 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
          {connector.id}
        </td>
        <td className="px-2 sm:px-3 py-2 sm:py-3 w-24 whitespace-nowrap">
          <StatusBadge status={connector.ocppStatus === 'Preparing' ? connector.ocppStatus : evse.status} />
        </td>
        <td className="px-2 sm:px-3 py-2 sm:py-3 w-16 text-xs sm:text-sm text-gray-500 whitespace-nowrap">
          {connector.physicalReference ?? '–'}
        </td>
        <td className="px-2 sm:px-3 py-2 sm:py-3 w-24 whitespace-nowrap">
          <PowerTypeDisplay powerType={connector.powerType} />
        </td>
        {showOrder && <td className="px-2 sm:px-3 py-2 sm:py-3 w-14">{orderSelect}</td>}
        {showLabel && <td className={`px-2 sm:px-3 py-2 sm:py-3 ${labelHeader === 'Label' ? 'w-14' : 'w-20'}`}>{nicknameInput}</td>}
      </tr>

      {/* ── Mobile: card row ───────────────────────────────────────────── */}
      <tr className={`sm:hidden border-b border-gray-100 ${connector.isSelected ? 'bg-gray-50' : 'bg-white'}`}>
        <td colSpan={8} className="px-3 py-3">
          <div className="flex items-start gap-3">
            {!readOnly && checkbox}
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="font-mono text-xs text-gray-700">{evse.uid}</span>
                <span className="font-mono text-xs text-gray-500">{evse.id}</span>
                <span className="text-xs text-gray-500">ID: {connector.id}</span>
                <StatusBadge status={connector.ocppStatus === 'Preparing' ? connector.ocppStatus : evse.status} />
                <PowerTypeDisplay powerType={connector.powerType} />
              </div>
              {(showOrder || showLabel) && (
                <div className="grid grid-cols-2 gap-2">
                  {showOrder && <div>
                    <p className="mb-0.5 text-xs text-gray-400">Order</p>
                    {orderSelect}
                  </div>}
                  {showLabel && <div>
                    <p className="mb-0.5 text-xs text-gray-400">{labelHeader}</p>
                    {nicknameInput}
                  </div>}
                </div>
              )}
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const normalized = status?.replace(/^_+/, '') ?? 'UNKNOWN';
  const styles: Record<string, string> = {
    AVAILABLE:      'border-blue-600 text-blue-700',
    PREPARING:      'border-cyan-400 text-cyan-500',
    CHARGING:       'border-green-600 text-green-700',
    SUSPENDEDEV:    'border-lime-500 text-lime-600',
    SUSPENDEDEVSE:  'border-lime-400 text-lime-500',
    FINISHING:      'border-teal-500 text-teal-600',
    RESERVED:       'border-purple-500 text-purple-600',
    FAULTED:        'border-red-500 text-red-600',
    UNAVAILABLE:    'border-gray-600 text-gray-700',
    INOPERATIVE:    'border-red-400 text-red-500',
    BLOCKED:        'border-red-400 text-red-500',
    OFFLINE:        'border-gray-400 text-gray-500',
    UNKNOWN:        'border-gray-400 text-gray-500',
  };
  const cls = styles[normalized.toUpperCase()] ?? 'border-gray-400 text-gray-500';
  return (
    <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold tracking-wide ${cls}`}>
      {normalized.toUpperCase()}
    </span>
  );
}

function PowerTypeDisplay({ powerType }: { powerType: string }) {
  if (!powerType) return <span className="text-sm text-gray-400">–</span>;
  const label = powerType
    .replace('AC_', 'AC ')
    .replace('DC_', 'DC ')
    .replace('_PHASE', '-Phase')
    .replace('_', ' ');
  return (
    <span className="flex items-center gap-1 text-sm text-gray-700">
      <svg className="h-3.5 w-3.5 shrink-0 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
      {label}
    </span>
  );
}
